import { useCallback, useMemo, useState } from 'react';
import { TransferHistory, SortConfig } from '../types/transfer.types';

export const useSortConfig = (histories: TransferHistory[]) => {
    const [sortConfig, setSortConfig] = useState<SortConfig>({
        columnKey: null,
        direction: null,
    });

    const handleSort = useCallback((columnKey: string) => {
        setSortConfig((prev) => {
            if (prev.columnKey === columnKey) {
                if (prev.direction === 'asc') return { columnKey, direction: 'desc' };
                if (prev.direction === 'desc') return { columnKey: null, direction: null };
            }
            return { columnKey, direction: 'asc' };
        });
    }, []);

    const sortedHistories = useMemo(() => {
        if (!histories || !sortConfig.columnKey || !sortConfig.direction) {
            return histories || [];
        }

        return [...histories].sort((a, b) => {
            const key = sortConfig.columnKey as keyof TransferHistory;
            const aVal = a[key];
            const bVal = b[key];

            const aEmpty = aVal == null || aVal === '';
            const bEmpty = bVal == null || bVal === '';

            if (aEmpty && bEmpty) return 0;
            if (aEmpty) return sortConfig.direction === 'asc' ? 1 : -1;
            if (bEmpty) return sortConfig.direction === 'asc' ? -1 : 1;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            const aStr = String(aVal);
            const bStr = String(bVal);
            return sortConfig.direction === 'asc'
                ? aStr.localeCompare(bStr)
                : bStr.localeCompare(aStr);
        });
    }, [histories, sortConfig]);

    return { sortConfig, handleSort, sortedHistories };
};
